import React, { useState, useEffect } from 'react';
// @ts-ignore
const XLSX = require('xlsx');
// @ts-ignore
const ExcelJS = require('exceljs');

interface TrueViewTableProps {
  newData?: any;
  onGoBack?: () => void;
  errorMessage?: string | null;
}

const TrueViewTable: React.FC<TrueViewTableProps> = ({ newData, onGoBack, errorMessage }) => {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [dateSortOrder, setDateSortOrder] = useState<'asc' | 'desc'>('asc');
  const [marketData, setMarketData] = useState<any>(null);
  const [evaluationData, setEvaluationData] = useState<any>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [goBackLoading, setGoBackLoading] = useState(false);
  const [pullMarketFilter, setPullMarketFilter] = useState<string>('All');
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [marketLoading, setMarketLoading] = useState(false);
  const [completionFilter, setCompletionFilter] = useState<string>('All');
  const [dmMarketFilter, setDmMarketFilter] = useState<string>('All');
  const [activeDmTab, setActiveDmTab] = useState<string>('');
  const [dmCompletionFilter, setDmCompletionFilter] = useState<string>('All');
  const [lastBotStatus, setLastBotStatus] = useState<any>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('current');
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear() + '');
  const [appliedMonth, setAppliedMonth] = useState<string>('current');
  const [appliedYear, setAppliedYear] = useState<string>(new Date().getFullYear() + '');
  const handleApply = () => {
    setAppliedMonth(selectedMonth);
    setAppliedYear(selectedYear);
  };

  const handleMonthChange = (value: string) => {
    setSelectedMonth(value);
    if (value === 'current') {
      setSelectedYear(new Date().getFullYear() + '');
    }
  };

  const [selectedDM, setSelectedDM] = useState<string | null>(null);

  useEffect(() => {
    const fetchLastBotStatus = async () => {
      try {
        const apiBaseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5001' : 'https://api.delightrgext.xyz';
        const response = await fetch(`${apiBaseUrl}/api/bot/trueview/bot/status/last-successful`);
        const data = await response.json();
        
        if (response.ok && data.success) {
          setLastBotStatus(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch last bot status:', error);
      }
    };
    
    fetchLastBotStatus();
  }, [appliedMonth, appliedYear]);
  
  useEffect(() => {
    const fetchEvaluationData = async () => {
      try {
        setEvaluationLoading(true);
        const apiBaseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5001' : 'https://api.delightrgext.xyz';
        const currentDate = new Date();
        const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
        const currentYear = currentDate.getFullYear().toString();
        
        const useMonth = appliedMonth === 'current' ? currentMonth : (appliedMonth || currentMonth);
        const useYear = appliedYear || currentYear;
        
        let dateParam = '';
        console.log('Debug - appliedMonth:', appliedMonth, 'useMonth:', useMonth, 'currentMonth:', currentMonth, 'useYear:', useYear, 'currentYear:', currentYear);
        
        if (appliedMonth === 'current' && useYear === currentYear) {
          dateParam = currentDate.toISOString().split('T')[0];
          console.log('Using current date:', dateParam);
        } else {
          const nextMonth = parseInt(useMonth) + 1;
          const nextYear = nextMonth > 12 ? parseInt(useYear) + 1 : parseInt(useYear);
          const adjustedMonth = nextMonth > 12 ? 1 : nextMonth;
          dateParam = `${nextYear}-${adjustedMonth.toString().padStart(2, '0')}-01`;
          console.log('Using next month date:', dateParam);
        }
        
        const response = await fetch(`${apiBaseUrl}/api/bot/trueview/data?name=List+of+Evaluations.json&date=${dateParam}`);
        
        if (response.status === 304) {
          // Use cached data - set evaluationLoading to false
          setEvaluationLoading(false);
          return;
        }
        
        const data = await response.json();
        
        if (response.ok && data.success && data.data) {
          setEvaluationData(data);
          setDataError(null);
          setGoBackLoading(false);
          setEvaluationLoading(false);
        } else {
          setEvaluationData(null);
          setGoBackLoading(false);
          // Parse the error message to show user-friendly text
          if (data.message && data.message.includes('Date folder') && data.message.includes('not found')) {
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            const currentMonth = appliedMonth || (new Date().getMonth() + 1).toString().padStart(2, '0');
            const monthName = monthNames[parseInt(currentMonth) - 1];
            const currentYear = appliedYear || new Date().getFullYear().toString();
            setDataError(`No data found for ${monthName} ${currentYear}. Please select a different month or year.`);
          } else {
            setDataError(`No data available for the selected period. Please try a different date.`);
          }
        }
      } catch (error) {
        console.error('Failed to fetch evaluation data:', error);
        setEvaluationData(null);
        setDataError(`No data available for the selected year. Please try a different year.`);
        setEvaluationLoading(false);
      }
    };
    
    fetchEvaluationData();
  }, [appliedMonth, appliedYear]);

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        setMarketLoading(true);
        const apiBaseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5001' : 'https://api.delightrgext.xyz';
        const currentDate = new Date();
        const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');
        const currentYear = currentDate.getFullYear().toString();
        
        const useMonth = appliedMonth === 'current' ? currentMonth : (appliedMonth || currentMonth);
        const useYear = appliedYear || currentYear;
        
        let dateParam = '';
        if (appliedMonth === 'current' && useYear === currentYear) {
          dateParam = currentDate.toISOString().split('T')[0];
        } else {
          const nextMonth = parseInt(useMonth) + 1;
          const nextYear = nextMonth > 12 ? parseInt(useYear) + 1 : parseInt(useYear);
          const adjustedMonth = nextMonth > 12 ? 1 : nextMonth;
          dateParam = `${nextYear}-${adjustedMonth.toString().padStart(2, '0')}-01`;
        }
        
        const response = await fetch(`${apiBaseUrl}/api/bot/trueview/data?name=market_dm_stores.json&date=${dateParam}`);
        
        if (response.status === 304) {
          // Use cached data - set evaluationLoading to false
          setMarketLoading(false);
          return;
        }
        
        const data = await response.json();
        
        if (response.ok && data.success && data.data) {
          setMarketData(data);
          if (!dataError) setDataError(null);
          setMarketLoading(false);
        } else {
          setMarketData(null);
          setMarketLoading(false);
          if (!dataError) {
            // Parse the error message to show user-friendly text
            if (data.message && data.message.includes('Date folder') && data.message.includes('not found')) {
              const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
              const currentMonth = appliedMonth || (new Date().getMonth() + 1).toString().padStart(2, '0');
              const monthName = monthNames[parseInt(currentMonth) - 1];
              const currentYear = appliedYear || new Date().getFullYear().toString();
              setDataError(`No data found for ${monthName} ${currentYear}. Please select a different month or year.`);
            } else {
              setDataError(`No data available for the selected period. Please try a different date.`);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch market data:', error);
        setMarketData(null);
        setMarketLoading(false);
        if (!dataError) setDataError(`No data available for the selected year. Please try a different year.`);
      }
    };
    
    fetchMarketData();
  }, [appliedMonth, appliedYear, dataError]);

  const markets = marketData?.data ? Object.keys(marketData.data) : [];
  const processedMarkets = markets.map(market => {
    if (market === 'NC West' || market === 'NC East') return 'North Carolina';
    if (market === 'OHWV' || market === 'Pitts') return 'Tri-State';
    if (market === 'VA Beach') return 'Virginia';
    return market;
  }).filter((market, index, arr) => arr.indexOf(market) === index);

  // Set default active tab when both data sources are ready
  useEffect(() => {
    if (marketData && evaluationData && processedMarkets.length > 0 && !activeTab) {
      setActiveTab('Summary');
    }
    if (processedMarkets.length > 0 && !activeDmTab) {
      setActiveDmTab('All');
    }
  }, [marketData, evaluationData, processedMarkets, activeTab, activeDmTab]);

  const getStoresForMarket = (market: string) => {
    if (market === 'North Carolina') {
      const ncWestStores = getStoresForOriginalMarket('NC West');
      const ncEastStores = getStoresForOriginalMarket('NC East');
      return [...ncWestStores, ...ncEastStores];
    }
    if (market === 'Tri-State') {
      const ohwvStores = getStoresForOriginalMarket('OHWV');
      const pittsStores = getStoresForOriginalMarket('Pitts');
      return [...ohwvStores, ...pittsStores];
    }
    if (market === 'Virginia') {
      return getStoresForOriginalMarket('VA Beach');
    }
    return getStoresForOriginalMarket(market);
  };

  const getStoresForOriginalMarket = (market: string) => {
    if (!market || !marketData?.data?.[market as keyof typeof marketData.data]) return [];
    const marketData_typed = marketData.data[market as keyof typeof marketData.data];
    const stores: any[] = [];
    Object.values(marketData_typed).forEach((dmStores: any) => {
      stores.push(...dmStores);
    });
    return stores;
  };

  const getUnitNumber = (storeNumber: number) => {
    const dataToUse = evaluationData || newData;
    if (!dataToUse?.data?.data) return 'N/A';
    const allStates = Object.values(dataToUse.data.data);
    for (const state of allStates) {
      const stateData = state as any;
      if (stateData[storeNumber]) {
        const unitData = stateData[storeNumber][0];
        return unitData?.['Unit Number'] || 'N/A';
      }
    }
    return 'N/A';
  };

  const getGlobalDate = (storeNumber: number) => {
    const dataToUse = evaluationData || newData;
    if (!dataToUse?.data?.data) return 'N/A';
    const allStates = Object.values(dataToUse.data.data);
    for (const state of allStates) {
      const stateData = state as any;
      if (stateData[storeNumber]) {
        const unitData = stateData[storeNumber][0];
        const globalDate = unitData?.['Global Date'];
        if (globalDate && typeof globalDate === 'number') {
          const date = new Date((globalDate - 25569) * 86400 * 1000);
          return date.toLocaleDateString();
        }
        return 'N/A';
      }
    }
    return 'N/A';
  };

  const getState = (storeNumber: number) => {
    const dataToUse = evaluationData || newData;
    if (!dataToUse?.data?.data) return 'N/A';
    const allStates = Object.values(dataToUse.data.data);
    for (const state of allStates) {
      const stateData = state as any;
      if (stateData[storeNumber]) {
        const unitData = stateData[storeNumber][0];
        const stateValue = unitData?.['State'] || 'N/A';
        if (activeTab === 'Tri-State' && ['OH', 'WV', 'PA'].includes(stateValue)) {
          return 'TS';
        }
        if (activeTab === 'Long Island' && stateValue === 'IN') {
          return 'NY';
        }
        return stateValue;
      }
    }
    return 'N/A';
  };

  const getDistrictManager = (storeNumber: number) => {
    const dataToUse = evaluationData || newData;
    if (!dataToUse?.data?.data) return 'N/A';
    const allStates = Object.values(dataToUse.data.data);
    for (const state of allStates) {
      const stateData = state as any;
      if (stateData[storeNumber]) {
        const unitData = stateData[storeNumber][0];
        const dm = unitData?.['District Manager'] || 'N/A';
        return dm === 'OPEN' ? 'Selecia McMillian' : dm;
      }
    }
    return 'N/A';
  };

  const downloadExcel = async () => {
    try {
      setDownloadLoading(true);
      const workbook = new ExcelJS.Workbook();
      
      // Summary Sheet
      const summarySheet = workbook.addWorksheet('Summary');
      summarySheet.addRow(['Market', 'No. of Stores', 'Not Completed', 'Completion Rate']);
      
      processedMarkets.forEach(market => {
        const marketStores = getStoresForMarket(market);
        const totalStores = marketStores.length;
        const completedStores = marketStores.filter(store => getGlobalDate(store["Store Number"]) !== 'N/A').length;
        const notCompleted = totalStores - completedStores;
        const completionRate = totalStores > 0 ? Math.round((completedStores / totalStores) * 100) : 0;
        
        const row = summarySheet.addRow([market, totalStores, notCompleted, `${completionRate}%`]);
        
        // Center align all cells in the row
        row.eachCell((cell: any) => {
          cell.alignment = { horizontal: 'center' };
        });
        
        // Color code based on completion rate
        const fillColor = completionRate < 50 ? 'FFFF0000' : 
                         completionRate < 80 ? 'FFFFFF00' : 'FF00FF00';
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: fillColor }
        };
      });
      
      // District Manager Summary in Summary Sheet
      summarySheet.addRow([]);
      const dmHeaderRow = summarySheet.addRow(['District Manager Summary']);
      dmHeaderRow.font = { bold: true, size: 14 };
      summarySheet.addRow(['District Manager', 'Market', 'Total Stores', 'Completed', 'Not Completed']);
      
      getDMData().forEach(dmInfo => {
        const completed = dmInfo.totalStores - dmInfo.notCompleted;
        const row = summarySheet.addRow([dmInfo.dm, dmInfo.market, dmInfo.totalStores, completed, dmInfo.notCompleted]);
        
        // Center align all cells in the row
        row.eachCell((cell: any) => {
          cell.alignment = { horizontal: 'center' };
        });
        
        // Color code based on completion status
        const fillColor = dmInfo.notCompleted > 0 ? 'FFFFF5F5' : 'FFF0FDF4';
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: fillColor }
        };
      });
      
      // Pull Sheet (Completed stores only)
      const pullSheet = workbook.addWorksheet('Pull');
      pullSheet.addRow(['Restaurant', 'Unit', 'State', 'Market', 'DM', 'Global Date']);
      
      const allCompletedStores: any[] = [];
      processedMarkets.forEach(market => {
        const marketStores = getStoresForMarket(market);
        marketStores.forEach(store => {
          const globalDate = getGlobalDate(store["Store Number"]);
          if (globalDate !== 'N/A') {
            allCompletedStores.push({ ...store, market });
          }
        });
      });
      
      allCompletedStores.forEach(store => {
        const state = getState(store["Store Number"]);
        const displayState = ['OH', 'WV', 'PA'].includes(state) ? 'TS' : (state === 'IN' ? 'NY' : state);
        
        const row = pullSheet.addRow([
          `${store["Store Number"]} - ${store["Store Name"]}`,
          getUnitNumber(store["Store Number"]),
          displayState,
          store.market,
          getDistrictManager(store["Store Number"]),
          getGlobalDate(store["Store Number"])
        ]);
        
        // Center align all cells in the row
        row.eachCell((cell: any) => {
          cell.alignment = { horizontal: 'center' };
        });
        
        // Light blue background for completed stores
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF0F8FF' }
        };
      });
      
      // Style headers for Summary and Pull sheets
      [summarySheet, pullSheet].forEach(sheet => {
        sheet.getRow(1).font = { bold: true };
        sheet.getRow(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE5E7EB' }
        };
        // Center align header cells
        sheet.getRow(1).eachCell((cell: any) => {
          cell.alignment = { horizontal: 'center' };
        });
        // Set column widths
        sheet.columns.forEach((column: any) => {
          column.width = 25;
        });
      });
      
      // Style DM Summary headers in Summary sheet
      const dmHeaderRowIndex = summarySheet.rowCount - getDMData().length;
      summarySheet.getRow(dmHeaderRowIndex).font = { bold: true };
      summarySheet.getRow(dmHeaderRowIndex).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE5E7EB' }
      };
      // Center align DM Summary header cells
      summarySheet.getRow(dmHeaderRowIndex).eachCell((cell: any) => {
        cell.alignment = { horizontal: 'center' };
      });
      
      // Individual Market Sheets
      processedMarkets.forEach(market => {
        const marketSheet = workbook.addWorksheet(market);
        marketSheet.addRow(['Restaurant', 'Unit', 'State', 'DM', 'Global Date']);
        
        const marketStores = getStoresForMarket(market);
        marketStores.forEach(store => {
          const globalDate = getGlobalDate(store["Store Number"]);
          const unit = getUnitNumber(store["Store Number"]);
          const isIncomplete = unit === 'N/A' || globalDate === 'N/A';
          const isCompleted = globalDate !== 'N/A';
          
          const row = marketSheet.addRow([
            `${store["Store Number"]} - ${store["Store Name"]}`,
            unit,
            getState(store["Store Number"]),
            (store["DM"]) || 'N/A',
            globalDate
          ]);
          
          // Center align all cells in the row
          row.eachCell((cell: any) => {
            cell.alignment = { horizontal: 'center' };
          });
          
          // Color code based on completion status
          const fillColor = isIncomplete ? 'FFF8D7DA' : 
                           isCompleted ? 'FFF0F8FF' : 'FFFFFFFF';
          row.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: fillColor }
          };
        });
        
        // Style headers and set column widths
        marketSheet.getRow(1).font = { bold: true };
        marketSheet.getRow(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE5E7EB' }
        };
        // Center align header cells
        marketSheet.getRow(1).eachCell((cell: any) => {
          cell.alignment = { horizontal: 'center' };
        });
        marketSheet.columns.forEach((column: any) => {
          column.width = 25;
        });
      });
      
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TrueView_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Excel download failed:', error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const getDMData = () => {
    const dmData: { [key: string]: { totalStores: number; notCompleted: number; market: string } } = {};
    
    processedMarkets.forEach(market => {
      const marketStores = getStoresForMarket(market);
      marketStores.forEach(store => {
        const storeNumber = store["Store Number"];
        let dm = store["DM"] || 'N/A'; // Get DM directly from market data
        
        if (dm !== 'N/A') {
          if (!dmData[dm]) {
            dmData[dm] = { totalStores: 0, notCompleted: 0, market };
          }
          dmData[dm].totalStores++;
          
          // Check if store exists in evaluation data with valid Global Date
          const hasValidEvaluation = evaluationData?.data?.data && 
            Object.values(evaluationData.data.data).some((state: any) => 
              state[storeNumber] && 
              state[storeNumber][0]?.['Global Date'] && 
              typeof state[storeNumber][0]['Global Date'] === 'number'
            );
          
          if (!hasValidEvaluation) {
            dmData[dm].notCompleted++;
          }
        }
      });
    });
    
    return Object.entries(dmData).map(([dm, data]) => ({ dm, ...data }));
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if ((!newData || !newData.data) && !marketData && !errorMessage && !dataError && !evaluationLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', backgroundColor: 'white', padding: '48px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '4px solid #e5e7eb', 
            borderTop: '4px solid #2563eb', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <h3 style={{ fontSize: '18px', color: '#374151', margin: '0 0 8px 0' }}>Loading Evaluations...</h3>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Please wait while we process the data</p>
          <style>
            {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
          </style>
        </div>
      </div>
    );
  }

  if (dataError || errorMessage) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: '20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#608ac4ff', color: 'white', padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h1 style={{ color: 'white', fontSize: '28px', fontWeight: 'bold', margin: '0 0 8px 0' }}>TrueView Evaluations</h1>
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '16px', fontWeight: '500' }}>Restaurant-data</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: 'rgba(255, 255, 255, 0.9)' }}>Month:</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => handleMonthChange(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      color: '#1f2937'
                    }}
                  >
                    <option value="current">Current Month</option>
                    {Array.from({length: 12}, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(0, i).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: 'rgba(255, 255, 255, 0.9)' }}>Year:</label>
                  <select
                    value={selectedYear || new Date().getFullYear()}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      color: '#1f2937'
                    }}
                  >
                    {Array.from({length: 7}, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                  <button
                    onClick={handleApply}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginLeft: '8px'
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <div style={{ 
                width: '48px', 
                height: '48px', 
                backgroundColor: '#fef2f2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <span style={{ fontSize: '24px', color: '#dc2626' }}>⚠️</span>
              </div>
              <h3 style={{ fontSize: '18px', color: '#dc2626', margin: '0 0 8px 0' }}>Data Not Found</h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 24px 0' }}>{dataError || errorMessage}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: isMobile ? '10px' : '20px', position: 'relative' }}>
      {(evaluationLoading || marketLoading) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px'
            }}>
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#3b82f6',
                    borderRadius: '50%',
                    animation: `bounce 1.4s ease-in-out ${i * 0.16}s infinite both`
                  }}
                />
              ))}
            </div>
            <div style={{
              textAlign: 'center'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 8px 0'
              }}>Loading</h3>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0
              }}>Fetching data...</p>
            </div>
          </div>
          <style>
            {`
              @keyframes bounce {
                0%, 80%, 100% {
                  transform: scale(0);
                }
                40% {
                  transform: scale(1);
                }
              }
            `}
          </style>
        </div>
      )}
      <div style={{ maxWidth: isMobile ? '100%' : '1400px', margin: '0 auto' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          <div style={{ backgroundColor: '#608ac4ff', color: 'white', padding: isMobile ? '16px' : '24px' }}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '16px' : '0' }}>
              <div>
                <h1 style={{ color: 'white', fontSize: isMobile ? '24px' : '28px', fontWeight: 'bold', margin: '0 0 8px 0' }}>TrueView Evaluations</h1>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : '16px' }}>
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: isMobile ? '14px' : '16px', fontWeight: '500' }}>Restaurant-data</p>
                  <p style={{ color: '#f3f3f3ff', margin: 0, fontSize: isMobile ? '12px' : '16px', opacity: 0.9 }}>
                    Last Updated: {(() => {
                      if (lastBotStatus?.message) {
                        const match = lastBotStatus.message.match(/(\w{3} \w{3} \d{2} \d{4} \d{2}:\d{2}:\d{2}) GMT-0500/);
                        if (match) {
                          const dateStr = match[1] + ' GMT-0500';
                          const date = new Date(dateStr);
                          const nyTime = new Date(date.getTime() - (5 * 60 * 60 * 1000));
                          return new Intl.DateTimeFormat('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: false
                          }).format(nyTime);
                        }
                      }
                      return new Intl.DateTimeFormat('en-US', {
                        timeZone: 'America/New_York',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                      }).format(new Date());
                    })()} 
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '500', color: 'rgba(255, 255, 255, 0.9)' }}>Month:</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => handleMonthChange(e.target.value)}
                  style={{
                    padding: isMobile ? '6px 10px' : '8px 12px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '14px',
                    outline: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#1f2937'
                  }}
                >
                  <option value="current">Current Month</option>
                  {Array.from({length: 12}, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
                <label style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '500', color: 'rgba(255, 255, 255, 0.9)' }}>Year:</label>
                <select
                  value={selectedYear || new Date().getFullYear()}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{
                    padding: isMobile ? '6px 10px' : '8px 12px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '14px',
                    outline: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: '#1f2937'
                  }}
                >
                  {Array.from({length: 7}, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return <option key={year} value={year}>{year}</option>;
                  })}
                </select>
                <button
                  onClick={handleApply}
                  style={{
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginLeft: '8px'
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>

          <div style={{ padding: isMobile ? '16px' : '24px' }}>
            <div style={{ 
              borderBottom: '2px solid #e5e7eb', 
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', paddingBottom: '8px' }}>
                <button
                  onClick={() => setActiveTab('Summary')}
                  style={{
                    padding: isMobile ? '8px 12px' : '10px 16px',
                    border: 'none',
                    backgroundColor: activeTab === 'Summary' ? '#2563eb' : 'transparent',
                    color: activeTab === 'Summary' ? 'white' : '#6b7280',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: activeTab === 'Summary' ? '600' : '500',
                    fontSize: isMobile ? '12px' : '14px',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Summary
                </button>
                <button
                  onClick={() => setActiveTab('Pull')}
                  style={{
                    padding: isMobile ? '8px 12px' : '10px 16px',
                    border: 'none',
                    backgroundColor: activeTab === 'Pull' ? '#2563eb' : 'transparent',
                    color: activeTab === 'Pull' ? 'white' : '#6b7280',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: activeTab === 'Pull' ? '600' : '500',
                    fontSize: isMobile ? '12px' : '14px',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Pull
                </button>
                {processedMarkets.map(market => (
                  <button
                    key={market}
                    onClick={() => setActiveTab(market)}
                    style={{
                      padding: isMobile ? '8px 12px' : '10px 16px',
                      border: 'none',
                      backgroundColor: activeTab === market ? '#2563eb' : 'transparent',
                      color: activeTab === market ? 'white' : '#6b7280',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: activeTab === market ? '600' : '500',
                      fontSize: isMobile ? '12px' : '14px',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {market}
                  </button>
                ))}
              </div>
              <button
                onClick={downloadExcel}
                disabled={downloadLoading}
                style={{
                  padding: isMobile ? '8px 16px' : '10px 20px',
                  backgroundColor: downloadLoading ? '#94a3b8' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: downloadLoading ? 'not-allowed' : 'pointer',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
              >
                📊 {downloadLoading ? 'Downloading...' : 'Download Excel'}
              </button>
            </div>

            {activeTab === 'Summary' ? (
              <>
                <div style={{ 
                  overflowX: 'auto', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Market</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'center', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>No. of Stores</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'center', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Not completed</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'center', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Completion Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedMarkets.map(market => {
                        const marketStores = getStoresForMarket(market);
                        const totalStores = marketStores.length;
                        const completedStores = marketStores.filter(store => getGlobalDate(store["Store Number"]) !== 'N/A').length;
                        const notCompleted = totalStores - completedStores;
                        const completionRate = totalStores > 0 ? Math.round((completedStores / totalStores) * 100) : 0;
                        
                        return (
                          <tr key={market} style={{ 
                            borderTop: '1px solid #e2e8f0',
                            backgroundColor: completionRate < 50 ? '#ffcccc' : 
                                           completionRate < 80 ? '#eceecdff' : '#acebe2ff'
                          }}>
                            <td style={{ 
                              padding: '18px 20px', 
                              fontSize: '15px', 
                              fontWeight: '500', 
                              color: '#1f2937'
                            }}>{market}</td>
                            <td style={{ 
                              padding: '18px 20px', 
                              fontSize: '15px', 
                              color: '#374151', 
                              textAlign: 'center',
                              fontWeight: '500'
                            }}>{totalStores}</td>
                            <td style={{ 
                              padding: '18px 20px', 
                              fontSize: '15px', 
                              color: notCompleted > 0 ? '#dc2626' : '#16a34a', 
                              textAlign: 'center',
                              fontWeight: '500'
                            }}>{notCompleted}</td>
                            <td style={{ 
                              padding: '18px 20px', 
                              fontSize: '15px', 
                              color: completionRate < 50 ? '#dc2626' : completionRate < 80 ? '#85321eff' : '#16a34a', 
                              textAlign: 'center',
                              fontWeight: '600'
                            }}>{completionRate}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                <div style={{ 
                  marginTop: '32px',
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  maxHeight: '500px',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{ 
                    padding: '16px 20px', 
                    backgroundColor: '#f8fafc', 
                    borderBottom: '1px solid #e5e7eb',
                    flexShrink: 0
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#374151'
                      }}>District Manager Summary</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>Status:</label>
                        <select
                          value={dmCompletionFilter}
                          onChange={(e) => setDmCompletionFilter(e.target.value)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '12px',
                            outline: 'none',
                            backgroundColor: 'white',
                            color: '#374151',
                            minWidth: '120px'
                          }}
                        >
                          <option value="All">All DMs</option>
                          <option value="Completed">All Completed</option>
                          <option value="Not Completed">Has Incomplete</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', overflowX: 'auto' }}>
                      <button
                        key="All"
                        onClick={() => setActiveDmTab('All')}
                        style={{
                          padding: '6px 12px',
                          border: 'none',
                          backgroundColor: activeDmTab === 'All' ? '#2563eb' : 'transparent',
                          color: activeDmTab === 'All' ? 'white' : '#6b7280',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: activeDmTab === 'All' ? '600' : '500',
                          fontSize: '16px',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        All
                      </button>
                      {processedMarkets.map(market => (
                        <button
                          key={market}
                          onClick={() => setActiveDmTab(market)}
                          style={{
                            padding: '6px 12px',
                            border: 'none',
                            backgroundColor: activeDmTab === market ? '#2563eb' : 'transparent',
                            color: activeDmTab === market ? 'white' : '#6b7280',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: activeDmTab === market ? '600' : '500',
                            fontSize: '14px',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {market}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ 
                    overflowY: 'auto',
                    flex: 1
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ 
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#f8fafc',
                        zIndex: 1
                      }}>
                        <tr>
                          <th style={{ 
                            padding: '16px 20px', 
                            textAlign: 'left', 
                            fontSize: '13px', 
                            fontWeight: '600', 
                            color: '#374151', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>District Manager</th>
                          <th style={{ 
                            padding: '16px 20px', 
                            textAlign: 'center', 
                            fontSize: '13px', 
                            fontWeight: '600', 
                            color: '#374151', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>Market</th>
                          <th style={{ 
                            padding: '16px 20px', 
                            textAlign: 'center', 
                            fontSize: '13px', 
                            fontWeight: '600', 
                            color: '#374151', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>Total Stores</th>
                          <th style={{ 
                            padding: '16px 20px', 
                            textAlign: 'center', 
                            fontSize: '13px', 
                            fontWeight: '600', 
                            color: '#374151', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>Completed</th>
                          <th style={{ 
                            padding: '16px 20px', 
                            textAlign: 'center', 
                            fontSize: '13px', 
                            fontWeight: '600', 
                            color: '#374151', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>Not Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getDMData().filter(dmInfo => {
                          // Market filter
                          const marketMatch = activeDmTab === 'All' || dmInfo.market === activeDmTab;
                          
                          // Completion filter
                          let completionMatch = true;
                          if (dmCompletionFilter === 'Completed') {
                            completionMatch = dmInfo.notCompleted === 0;
                          } else if (dmCompletionFilter === 'Not Completed') {
                            completionMatch = dmInfo.notCompleted > 0;
                          }
                          
                          return marketMatch && completionMatch;
                        }).map((dmInfo, index) => (
                          <React.Fragment key={index}>
                            <tr style={{ 
                              borderTop: index === 0 ? 'none' : '1px solid #e2e8f0',
                              backgroundColor: dmInfo.notCompleted > 0 ? '#fff5f5' : '#f0fdf4'
                            }}>
                              <td style={{ 
                                padding: '18px 20px', 
                                fontSize: '15px', 
                                fontWeight: '500', 
                                color: '#1f2937',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }} onClick={() => setSelectedDM(selectedDM === dmInfo.dm ? null : dmInfo.dm)}>
                                <span style={{ fontSize: '12px' }}>
                                  {selectedDM === dmInfo.dm ? '▼' : '▶'}
                                </span>
                                {dmInfo.dm}
                              </td>
                              <td style={{ 
                                padding: '18px 20px', 
                                fontSize: '15px', 
                                color: '#374151', 
                                textAlign: 'center',
                                fontWeight: '500'
                              }}>{dmInfo.market}</td>
                              <td style={{ 
                                padding: '18px 20px', 
                                fontSize: '15px', 
                                color: '#374151', 
                                textAlign: 'center',
                                fontWeight: '500'
                              }}>{dmInfo.totalStores}</td>
                              <td style={{ 
                                padding: '18px 20px', 
                                fontSize: '15px', 
                                color: (dmInfo.totalStores - dmInfo.notCompleted) > 0 ? '#16a34a' : '#6b7280', 
                                textAlign: 'center',
                                fontWeight: '500'
                              }}>{dmInfo.totalStores - dmInfo.notCompleted}</td>
                              <td style={{ 
                                padding: '18px 20px', 
                                fontSize: '15px', 
                                color: dmInfo.notCompleted > 0 ? '#dc2626' : '#16a34a', 
                                textAlign: 'center',
                                fontWeight: '500'
                              }}>{dmInfo.notCompleted}</td>
                            </tr>
                            {selectedDM === dmInfo.dm && (
                              <tr>
                                <td colSpan={5} style={{ padding: '0', backgroundColor: '#f8fafc' }}>
                                  <div style={{ 
                                    margin: '0 20px 20px 20px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    overflow: 'hidden'
                                  }}>
                                    <div style={{
                                      padding: '12px 16px',
                                      backgroundColor: '#f1f5f9',
                                      borderBottom: '1px solid #e5e7eb',
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      color: '#374151'
                                    }}>
                                      {dmInfo.dm}'s Restaurants
                                    </div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr style={{ backgroundColor: '#f8fafc' }}>
                                          <th style={{ 
                                            padding: '12px 16px', 
                                            textAlign: 'left', 
                                            fontSize: '12px', 
                                            fontWeight: '600', 
                                            color: '#374151', 
                                            textTransform: 'uppercase',
                                            width: '22%'
                                          }}>Restaurant</th>
                                          <th style={{ 
                                            padding: '12px 16px', 
                                            textAlign: 'center', 
                                            fontSize: '12px', 
                                            fontWeight: '600', 
                                            color: '#374151', 
                                            textTransform: 'uppercase',
                                            width: '15%'
                                          }}>State</th>
                                          <th style={{ 
                                            padding: '12px 16px', 
                                            textAlign: 'center', 
                                            fontSize: '12px', 
                                            fontWeight: '600', 
                                            color: '#374151', 
                                            textTransform: 'uppercase',
                                            width: '13%'
                                          }}>Unit</th>
                                          <th style={{ 
                                            padding: '12px 16px', 
                                            textAlign: 'center', 
                                            fontSize: '12px', 
                                            fontWeight: '600', 
                                            color: '#374151', 
                                            textTransform: 'uppercase',
                                            width: '17%'
                                          }}>Status</th>
                                          <th style={{ 
                                            padding: '12px 16px', 
                                            textAlign: 'center', 
                                            fontSize: '12px', 
                                            fontWeight: '600', 
                                            color: '#374151', 
                                            textTransform: 'uppercase',
                                            width: '16%'
                                          }}>Global Date</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(() => {
                                          const dmStores: any[] = [];
                                          processedMarkets.forEach(market => {
                                            const marketStores = getStoresForMarket(market);
                                            marketStores.forEach(store => {
                                              const storeDM = store["DM"] || 'N/A';
                                              if (storeDM === dmInfo.dm) {
                                                dmStores.push({ ...store, market });
                                              }
                                            });
                                          });
                                          
                                          return dmStores.map((store, storeIndex) => {
                                            const globalDate = getGlobalDate(store["Store Number"]);
                                            const unit = getUnitNumber(store["Store Number"]);
                                            const isCompleted = globalDate !== 'N/A';
                                            
                                            return (
                                              <tr key={storeIndex} style={{ 
                                                borderTop: storeIndex === 0 ? 'none' : '1px solid #f0f0f0',
                                                backgroundColor: storeIndex % 2 === 0 ? '#fafafa' : 'white'
                                              }}>
                                                <td style={{ 
                                                  padding: '12px 16px', 
                                                  fontSize: '14px', 
                                                  color: '#374151'
                                                }}>
                                                  {store["Store Number"]} - {store["Store Name"]}
                                                </td>
                                                <td style={{ 
                                                  padding: '12px 16px', 
                                                  fontSize: '14px', 
                                                  color: '#374151',
                                                  textAlign: 'center'
                                                }}>
                                                  {(() => {
                                                    const state = getState(store["Store Number"]);
                                                    if (['OH', 'WV', 'PA'].includes(state)) return 'TS';
                                                    if (state === 'IN') return 'NY';
                                                    return state;
                                                  })()}
                                                </td>
                                                <td style={{ 
                                                  padding: '12px 16px', 
                                                  fontSize: '14px', 
                                                  color: '#374151',
                                                  textAlign: 'center'
                                                }}>
                                                  {unit}
                                                </td>
                                                <td style={{ 
                                                  padding: '12px 16px', 
                                                  fontSize: '12px', 
                                                  textAlign: 'center'
                                                }}>
                                                  <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontWeight: '600',
                                                    textTransform: 'uppercase',
                                                    backgroundColor: isCompleted ? '#dcfce7' : '#fee2e2',
                                                    color: isCompleted ? '#166534' : '#991b1b'
                                                  }}>
                                                    {isCompleted ? 'Complete' : 'Pending'}
                                                  </span>
                                                </td>
                                                <td style={{ 
                                                  padding: '12px 16px', 
                                                  fontSize: '14px', 
                                                  color: isCompleted ? '#16a34a' : '#dc2626',
                                                  fontWeight: '500',
                                                  textAlign: 'center'
                                                }}>
                                                  {globalDate}
                                                </td>
                                              </tr>
                                            );
                                          });
                                        })()
                                        }
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                </div>
              </>
            ) : activeTab === 'Pull' ? (
              <>
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Filter by Market:</label>
                  <select
                    value={pullMarketFilter}
                    onChange={(e) => setPullMarketFilter(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'white',
                      color: '#374151',
                      minWidth: '150px'
                    }}
                  >
                    <option value="All">All Markets</option>
                    {processedMarkets.map(market => (
                      <option key={market} value={market}>{market}</option>
                    ))}
                  </select>
                </div>
                <div style={{ 
                  overflowX: 'auto', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Restaurant</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Unit</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>State</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Market</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>DM</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Global Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const allCompletedStores: any[] = [];
                        processedMarkets.forEach(market => {
                          const marketStores = getStoresForMarket(market);
                          marketStores.forEach(store => {
                            const globalDate = getGlobalDate(store["Store Number"]);
                            if (globalDate !== 'N/A') {
                              allCompletedStores.push({ ...store, market });
                            }
                          });
                        });
                        
                        const filteredStores = pullMarketFilter === 'All' 
                          ? allCompletedStores 
                          : allCompletedStores.filter(store => store.market === pullMarketFilter);
                        
                        return filteredStores.map((store, index) => (
                          <tr key={index} style={{ 
                            borderTop: '1px solid #e2e8f0',
                            backgroundColor: '#f0f8ff'
                          }}>
                            <td style={{ 
                              padding: '16px 20px', 
                              fontSize: '14px', 
                              color: '#374151'
                            }}>
                              {store["Store Number"]} - {store["Store Name"]}
                            </td>
                            <td style={{ 
                              padding: '16px 20px', 
                              fontSize: '14px', 
                              color: '#374151'
                            }}>
                              {getUnitNumber(store["Store Number"])}
                            </td>
                            <td style={{ 
                              padding: '16px 20px', 
                              fontSize: '14px', 
                              color: '#374151'
                            }}>
                              {(() => {
                                const state = getState(store["Store Number"]);
                                if (['OH', 'WV', 'PA'].includes(state)) return 'TS';
                                if (state === 'IN') return 'NY';
                                return state;
                              })()}
                            </td>
                            <td style={{ 
                              padding: '16px 20px', 
                              fontSize: '14px', 
                              color: '#374151'
                            }}>
                              {store.market}
                            </td>
                            <td style={{ 
                              padding: '16px 20px', 
                              fontSize: '14px', 
                              color: '#374151'
                            }}>
                              {getDistrictManager(store["Store Number"])}
                            </td>
                            <td style={{ 
                              padding: '16px 20px', 
                              fontSize: '14px', 
                              color: '#374151'
                            }}>
                              {getGlobalDate(store["Store Number"])}
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </>
            ) : activeTab && processedMarkets.includes(activeTab) ? (
              <>
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Filter by Status:</label>
                  <select
                    value={completionFilter}
                    onChange={(e) => setCompletionFilter(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'white',
                      color: '#374151',
                      minWidth: '150px'
                    }}
                  >
                    <option value="All">All Stores</option>
                    <option value="Completed">Completed</option>
                    <option value="Not Completed">Not Completed</option>
                  </select>
                </div>
                <div style={{ 
                  overflowX: 'auto', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }} onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>Restaurant {sortOrder === 'asc' ? '↑' : '↓'}</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Unit</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>State</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>DM</th>
                        <th style={{ 
                          padding: '16px 20px', 
                          textAlign: 'left', 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#374151', 
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Global Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const currentStores = getStoresForMarket(activeTab);
                        const dateSortedStores = [...currentStores].sort((a, b) => {
                          const aNumber = a["Store Number"];
                          const bNumber = b["Store Number"];
                          return sortOrder === 'asc' ? aNumber - bNumber : bNumber - aNumber;
                        });
                        
                        let filteredStores = dateSortedStores;
                        if (completionFilter === 'Completed') {
                          filteredStores = dateSortedStores.filter(store => getGlobalDate(store["Store Number"]) !== 'N/A');
                        } else if (completionFilter === 'Not Completed') {
                          filteredStores = dateSortedStores.filter(store => getGlobalDate(store["Store Number"]) === 'N/A');
                        }
                        
                        return filteredStores.map((store, index) => {
                          const unit = getUnitNumber(store["Store Number"]);
                          const globalDate = getGlobalDate(store["Store Number"]);
                          const isIncomplete = unit === 'N/A' || globalDate === 'N/A';
                          const isCompleted = globalDate !== 'N/A';
                          
                          return (
                            <tr key={index} style={{ 
                              borderTop: '1px solid #e2e8f0',
                              backgroundColor: isIncomplete ? '#f8d7da' : 
                                             isCompleted ? '#f0f8ff' : 'transparent'
                            }}>
                              <td style={{ 
                                padding: '16px 20px', 
                                fontSize: '14px', 
                                color: '#374151'
                              }}>
                                {store["Store Number"]} - {store["Store Name"]}
                              </td>
                              <td style={{ 
                                padding: '16px 20px', 
                                fontSize: '14px', 
                                color: '#374151'
                              }}>
                                {getUnitNumber(store["Store Number"])}
                              </td>
                              <td style={{ 
                                padding: '16px 20px', 
                                fontSize: '14px', 
                                color: '#374151'
                              }}>
                                {getState(store["Store Number"])}
                              </td>
                              <td style={{ 
                                padding: '16px 20px', 
                                fontSize: '14px', 
                                color: '#374151'
                              }}>
                                {store["DM"] || 'N/A'}
                              </td>
                              <td style={{ 
                                padding: '16px 20px', 
                                fontSize: '14px', 
                                color: '#374151'
                              }}>
                                {getGlobalDate(store["Store Number"])}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div style={{ 
                padding: '48px', 
                textAlign: 'center',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <h3 style={{ 
                  fontSize: '18px', 
                  color: '#6b7280', 
                  margin: '0 0 16px 0' 
                }}>{activeTab ? 'No Stores Found' : 'Select a Market'}</h3>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#9ca3af', 
                  margin: 0 
                }}>{activeTab ? 'No stores available for this market' : 'Choose a market tab to view restaurants'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrueViewTable;